import React, { FC, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import Text from '@ingka/text';
import Badge from '@ingka/badge';
import Button from '@ingka/button';
import Tooltip from '@ingka/tooltip';
import arrowCloudIn from '@ingka/ssr-icon/paths/arrow-cloud-in';
import { useFormContext } from 'react-hook-form';
import useTranslation from '../../hooks/useTranslation';
import Loading, { LoadingBall } from '@ingka/loading';
import { useTypedSelector } from '../../store';
import {
  getLoading,
  selectProjectsCurrency,
} from '../../store/projects/selectors';
import { selectCurrencyArray } from '../../store/currency/selectors';
import { returnCountryObject } from '../../hooks/useLocalCurrency';
import { EUR } from '../../constants';
import { ProjectDirectiveViewProps } from './ProjectDirectiveViewProps';
import { exportDirectiveToPdf } from './exportDirectiveToPdf';
import {
  confidentialityColourMap,
  getFormattedDate,
} from '../ProjectListTable/utils';
import {
  FIELD_TO_TAB,
  formatMoneyWithEur,
  formatNumber,
  formatText,
  getDirectiveFields,
  mapDirective,
} from './mapDirective';
import DirectiveCell from './DirectiveCell';
import TwoColumnGrid from './TwoColumnGrid';
import SectionHeading from './SectionHeading';
import ListView from '@ingka/list-view';
const ProjectDirectiveView: FC<ProjectDirectiveViewProps> = ({
  project,
  onEditField,
}) => {
  const t = useTranslation();
  const loading = useTypedSelector(getLoading);
  const currencySymbol = useTypedSelector(selectProjectsCurrency);
  const currencyArray = useTypedSelector(selectCurrencyArray);
  const { watch } = useFormContext();
  const formValues = watch();
  const countryCode = formValues.countryCode ?? project.countryCode;
  const sortedFys = Object.keys(currencyArray).sort(
    (a, b) => parseInt(b.slice(2), 10) - parseInt(a.slice(2), 10),
  );
  const getEurRate = (fy?: string): number | undefined => {
    const fysToTry = [fy, ...sortedFys].filter(Boolean) as string[];
    for (const candidate of fysToTry) {
      const currencyObject = returnCountryObject(
        currencyArray[candidate],
        countryCode,
      );
      if (currencyObject) {
        return currencyObject.currencyCode === 'EUR'
          ? 1
          : currencyObject.currencyRate || 1;
      }
    }
    return undefined;
  };
  const isEurCurrency = currencySymbol === EUR;
  const fieldsToRender = useMemo(() => getDirectiveFields(project), [project]);
  const directive = mapDirective(
    { ...project, ...formValues },
    t,
    getEurRate,
    isEurCurrency,
  );
  const returnIsMissing = (
    rawValue: string | number | null | undefined,
  ): boolean => rawValue === undefined || rawValue === null || rawValue === '';

  const onEditHandler = (key: string): (() => void) | undefined => {
    if (project?.isep) return undefined;
    const mapping = FIELD_TO_TAB[key];
    if (!mapping || !onEditField) return undefined;

    return () => onEditField(mapping.tab, mapping.fieldName);
  };

  const handleExportToPdf = () => {
    exportDirectiveToPdf(
      directive.referenceId || '',
      directive.projectName || '',
    );
  };

  if (loading) {
    return (
      <Stack
        flex={1}
        justifyContent="center"
        alignItems="center"
        data-testid="project-directive-view-loading"
      >
        <Loading>
          <LoadingBall />
        </Loading>
      </Stack>
    );
  }

  return (
    <Stack
      pb={5}
      spacing={4}
      px={2}
      flex={1}
      data-testid="project-directive-view"
      sx={{
        overflowX: 'hidden',
        '& .list-view-item__title': { lineHeight: '18px' },
        '& .list-view-item__description': {
          fontSize: '0.875rem',
          lineHeight: '18px',
          marginTop: '4px',
        },
      }}
    >
      <ListView id={'directive-list'} size="small">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack spacing={1}>
            <Text
              tagName="h2"
              textStyle="Heading.M"
              style={{ fontWeight: 700 }}
            >
              {formatText(directive.projectName)}
            </Text>
            <Stack direction="row">
              <Badge
                label={t(`confidentialityLevels.${project.confidential}`)}
                size="small"
                colour={
                  confidentialityColourMap[project.confidential] || 'grey'
                }
                prefix="testid-directive-confidential "
              />
            </Stack>
          </Stack>
          {!project?.isep ? (
            <Tooltip
              tooltipText={t('labels.directiveExportDisabledTooltip')}
              describedById="directive-export-tooltip"
              position="bottom"
            >
              <span style={{ display: 'inline-flex' }}>
                <Button
                  size="small"
                  type="secondary"
                  ssrIcon={arrowCloudIn}
                  iconPosition="leading"
                  onClick={handleExportToPdf}
                  disabled={!project?.isep}
                  data-testid="directive-export-pdf-btn"
                  style={!project?.isep ? { pointerEvents: 'none' } : undefined}
                >
                  {t('labels.exportToPdf')}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <span style={{ display: 'inline-flex' }}>
              <Button
                size="small"
                type="secondary"
                ssrIcon={arrowCloudIn}
                iconPosition="leading"
                onClick={handleExportToPdf}
                disabled={!project?.isep}
                data-testid="directive-export-pdf-btn"
                style={!project?.isep ? { pointerEvents: 'none' } : undefined}
              >
                {t('labels.exportToPdf')}
              </Button>
            </span>
          )}
        </Stack>
        <Stack spacing={1.5}>
          <SectionHeading>{t('labels.sectionGeneral')}</SectionHeading>
          <TwoColumnGrid>
            {fieldsToRender?.general?.map(
              ({ field, translationKey, alternativeMissingLabel }) => {
                const isConfidentiality = field === 'confidentiality';
                const rawValue = isConfidentiality
                  ? project.confidential
                  : directive[field];
                const displayValue = isConfidentiality
                  ? formatText(
                      project.confidential
                        ? t(`confidentialityLevels.${project.confidential}`)
                        : project.confidential,
                    )
                  : formatText(directive[field]);
                const cellLabel = isConfidentiality
                  ? t('labels.Confidentiality')
                  : t(translationKey ?? `ppm.columns.${field}`);
                return (
                  <DirectiveCell
                    key={`${field}-directive-field`}
                    value={displayValue}
                    label={cellLabel}
                    isMissing={returnIsMissing(rawValue)}
                    onEdit={onEditHandler(field)}
                    avatar={field === 'projectOwner'}
                    directiveDisabled={project?.isep}
                    alternativeMissingLabel={
                      alternativeMissingLabel ?? undefined
                    }
                  />
                );
              },
            )}
          </TwoColumnGrid>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 1.5,
          }}
        >
          <Stack spacing={1.5}>
            <SectionHeading>{t('labels.sectionDates')}</SectionHeading>
            <Box>
              <DirectiveCell
                value={formatText(getFormattedDate(project?.isepDate))}
                optional
                alternativeMissingLabel={'Not set'}
                label={t('labels.isepDate')}
              />
              {fieldsToRender?.dates?.map(
                ({ field, translationKey, alternativeMissingLabel }) => (
                  <DirectiveCell
                    value={formatText(directive[field])}
                    label={t(translationKey ?? `labels.${field}`)}
                    isMissing={returnIsMissing(directive[field])}
                    onEdit={onEditHandler(field)}
                    directiveDisabled={project?.isep}
                    alternativeMissingLabel={
                      alternativeMissingLabel ?? undefined
                    }
                  />
                ),
              )}
            </Box>
          </Stack>
          <Stack spacing={1.5}>
            <SectionHeading>{t('labels.sectionDependencies')}</SectionHeading>
            <DirectiveCell
              value={formatText(directive.mergedWithReferenceId)}
              label={t('labels.dependenciesToOtherProjects')}
              isMissing={false}
              optional
              onEdit={onEditHandler('mergedWithReferenceId')}
              alternativeMissingLabel={'No project'}
              directiveDisabled={project?.isep}
            />
          </Stack>
        </Box>
        <Stack spacing={1.5}>
          <SectionHeading>{t('labels.sectionInvestment')}</SectionHeading>
          <TwoColumnGrid>
            {fieldsToRender?.investment?.map(
              ({ field, translationKey, alternativeMissingLabel }) => (
                <DirectiveCell
                  value={formatMoneyWithEur(
                    directive[field],
                    directive[`${field}Eur`],
                    currencySymbol,
                  )}
                  label={t(translationKey ?? `labels.${field}`)}
                  isMissing={returnIsMissing(directive[field])}
                  onEdit={onEditHandler(field)}
                  directiveDisabled={project?.isep}
                  alternativeMissingLabel={alternativeMissingLabel ?? undefined}
                />
              ),
            )}
            <Box sx={{ gridColumn: '1' }}>
              <DirectiveCell
                value={formatMoneyWithEur(
                  directive.totalProjectAmountInclExternalLease,
                  directive.totalProjectAmountInclExternalLeaseEur,
                  currencySymbol,
                )}
                onEdit={onEditHandler('totalProjectAmountInclExternalLease')}
                isMissing={directive.totalProjectAmountInclExternalLease === 0}
                directiveDisabled={project?.isep}
                label={t('labels.totalProjectAmountInclExternalLease')}
              />
            </Box>
          </TwoColumnGrid>
        </Stack>
        {fieldsToRender?.replenishmentAndFulfilment?.length > 0 && (
          <Stack spacing={1.5}>
            <SectionHeading>
              {t('labels.sectionReplenishmentAndFulfilment')}
            </SectionHeading>
            <Box>
              {fieldsToRender.replenishmentAndFulfilment.map(
                ({ field, translationKey, alternativeMissingLabel }) => (
                  <DirectiveCell
                    key={`${field}-directive-field`}
                    value={formatNumber(directive[field])}
                    label={t(translationKey ?? `ppm.columns.${field}`)}
                    isMissing={returnIsMissing(directive[field])}
                    onEdit={onEditHandler(field)}
                    directiveDisabled={project?.isep}
                    alternativeMissingLabel={
                      alternativeMissingLabel ?? undefined
                    }
                  />
                ),
              )}
            </Box>
          </Stack>
        )}
        {fieldsToRender?.people && (
          <Stack spacing={1.5}>
            <SectionHeading>{t('labels.sectionPeople')}</SectionHeading>
            <Box>
              {fieldsToRender?.people?.map(
                ({ field, translationKey, alternativeMissingLabel }) => (
                  <DirectiveCell
                    key={`${field}-directive-field`}
                    value={formatNumber(directive[field])}
                    label={t(translationKey ?? `ppm.columns.${field}`)}
                    isMissing={returnIsMissing(directive[field])}
                    onEdit={onEditHandler(field)}
                    directiveDisabled={project?.isep}
                    alternativeMissingLabel={
                      alternativeMissingLabel ?? undefined
                    }
                  />
                ),
              )}
            </Box>
          </Stack>
        )}
      </ListView>
    </Stack>
  );
};

export default ProjectDirectiveView;
